import { useQuery } from '@tanstack/react-query';
import type { InsightsOverviewResponse } from '@brand24-original/llm-types';
import { GET_INSIGHTS_OVERVIEW } from '../../query/insights/keys';
import { getInsightsOverview } from '../../services/insights/InsightsStatsService';
import { ChatType } from '../../../types/monitor/ChatType';

export interface InsightsOverviewParams {
  projectId: number;
  chatType: ChatType;
  from: Date | null;
  to: Date | null;
  monitorPromptIds?: number[];
  filterChatTypes?: string[];
  /**
   * The analyzed brand (project.firmName). Key-only: the backend reads it from the project, this just
   * forces a refetch when the ChangeKeywordBox switches it.
   */
  brand?: string;
}

export const useGetInsightsOverview = (params: InsightsOverviewParams | null) => {
  const fromTimestamp = params?.from?.getTime() ?? null;
  const toTimestamp = params?.to?.getTime() ?? null;

  const query = useQuery<InsightsOverviewResponse>({
    queryKey: [
      GET_INSIGHTS_OVERVIEW,
      params?.projectId,
      params?.chatType,
      fromTimestamp,
      toTimestamp,
      params?.monitorPromptIds,
      params?.filterChatTypes,
      params?.brand,
    ],
    // `enabled` gates on params/from/to, so the assertions below cannot fire at runtime.
    queryFn: () =>
      getInsightsOverview(
        params!.projectId,
        params!.chatType,
        params!.from!,
        params!.to!,
        params!.monitorPromptIds,
        params!.filterChatTypes
      ),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: !!params && !!params.from && !!params.to && !!params.chatType && !!params.projectId,
  });

  return {
    insightsOverview: query.data ?? null,
    // isPending, not isLoading: a DISABLED query (filters not hydrated yet) has isLoading=false in
    // react-query v5, which would flash the "no data" panel before the first fetch even starts.
    isLoading: query.isPending,
    isError: query.isError,
  };
};
